import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from '../lib/toast.js';

const TEST_DESTINATION = 'david@xsitegroup.ca';

// Dave 2026-06-12: type='petrocan' switches the dialog to use the
// PetroCan request template + Jim Schroder recipient. Default 'bulloch'
// preserves the existing Bulloch back-office flow.
export default function BullochConfigDialog({ open, record, site, onClose, onSent, type = 'bulloch' }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  // Default ON — failsafe so an actual config request doesn't fire by accident.
  const [testMode, setTestMode] = useState(true);
  const [requestType, setRequestType] = useState('new_client');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [sent, setSent] = useState(null);
  // The sent PDF, kept so it can be saved with a Save dialog that
  // remembers J:\Bulloch Username (Chrome/Edge remember the last folder
  // per picker id). savedAs = filename once saved.
  const [pdfFile, setPdfFile] = useState(null);
  const [savedAs, setSavedAs] = useState(null);
  // Downloading the merged Word preview (own flag so the Send button
  // doesn't get disabled while the user is just grabbing a preview).
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  // Optional user-edited .docx — when set, Send uploads this buffer
  // instead of asking the server to re-merge from the template.
  const [editedDocx, setEditedDocx] = useState(null);

  useEffect(() => {
    if (!open || !record?.id) return;
    setLoading(true);
    setPreviewError(null);
    setSent(null);
    setPdfFile(null);
    setSavedAs(null);
    setError(null);
    setErrorCode(null);
    setTestMode(true);
    setRequestType('new_client');
    setEditedDocx(null);
    setDownloadingDocx(false);
    // site_id is included whenever the dialog was opened from a
    // specific Site row so the preview reflects that site's address
    // / site_number / POS type instead of the customer's legacy
    // customer_details columns.
    const params = { contact_id: record.id };
    if (site && site.id) params.site_id = site.id;
    axios.get('/api/bulloch/preview', { params })
      .then((res) => setPreview(res.data))
      .catch((err) => setPreviewError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [open, record?.id, site?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  function downloadPdf(file) {
    const url = URL.createObjectURL(file.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  // Save dialog via the File System Access API. `id` makes the browser
  // reopen the folder used last time, so after the first save into
  // J:\Bulloch Username it opens there every time.
  async function savePdf(file, quiet = false) {
    if (!file) return;
    if (!window.showSaveFilePicker) { downloadPdf(file); return; }
    try {
      const handle = await window.showSaveFilePicker({
        id: 'bulloch-username',
        suggestedName: file.name,
        types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }]
      });
      const w = await handle.createWritable();
      await w.write(file.blob);
      await w.close();
      setSavedAs(handle.name || file.name);
    } catch (err) {
      // AbortError = user cancelled; SecurityError = needs a click first.
      if (!quiet && err.name !== 'AbortError') toast.error('Could not save the PDF', err.message);
    }
  }

  async function handleSend() {
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      let res;
      if (editedDocx) {
        // Multipart path — send the user-edited .docx so the server skips
        // the template merge and converts THIS file to PDF.
        const fd = new FormData();
        fd.append('contact_id', String(record.id));
        if (site && site.id) fd.append('site_id', String(site.id));
        fd.append('test_mode', testMode ? 'true' : 'false');
        fd.append('request_type', requestType);
        fd.append('type', type);
        fd.append('docx', editedDocx, editedDocx.name);
        res = await axios.post('/api/bulloch/send', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Original JSON path — server re-merges from template.
        const body = { contact_id: record.id, test_mode: testMode, request_type: requestType, type };
        if (site && site.id) body.site_id = site.id;
        res = await axios.post('/api/bulloch/send', body);
      }
      setSent(res.data);

      // Railway can't write to J:\Bulloch Username, so the PDF comes back
      // here: offer a Save dialog (remembers the folder), or fall back to
      // a normal download in browsers without one.
      if (res.data.pdf_base64 && res.data.pdf_filename) {
        try {
          const byteChars = atob(res.data.pdf_base64);
          const bytes = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
          const file = { blob: new Blob([bytes], { type: 'application/pdf' }), name: res.data.pdf_filename };
          setPdfFile(file);
          if (!window.showSaveFilePicker) downloadPdf(file);
          else savePdf(file, true);   // may need a click if the browser blocks it
        } catch (dlErr) {
          console.warn('Bulloch PDF prep failed:', dlErr);
        }
      }

      // Prominent confirmation that the email actually went out.
      const recipients = [res.data.to, ...(res.data.cc || [])].filter(Boolean).join(', ');
      const when = res.data.sent_at ? new Date(res.data.sent_at).toLocaleString() : new Date().toLocaleString();
      toast.success(
        'Configuration request sent',
        `Sent to ${recipients} at ${when}. Save the PDF to J:\\Bulloch Username\\`
      );
      if (onSent) onSent();
    } catch (err) {
      const reason = err.response?.data?.error || err.message;
      setError(reason);
      setErrorCode(err.response?.data?.code || null);
      toast.error('Configuration request failed', reason);
    } finally {
      setBusy(false);
    }
  }

  // Download the merged Word doc so the user can open it in Word and
  // (optionally) tweak wording before sending. The server endpoint
  // streams the .docx with a Content-Disposition attachment header —
  // we trigger the browser download via a temporary blob URL.
  async function handlePreviewDocx() {
    setDownloadingDocx(true);
    setError(null);
    setErrorCode(null);
    try {
      const body = { contact_id: record.id };
      if (site && site.id) body.site_id = site.id;
      const res = await axios.post('/api/bulloch/preview-docx', body, {
        responseType: 'blob'
      });
      // Pull filename out of Content-Disposition so we keep the same
      // naming convention as the PDF.
      const disposition = res.headers['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match ? match[1] : 'bulloch_preview.docx';
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Word preview downloaded', filename);
    } catch (err) {
      // For a blob response axios won't auto-parse the error body — try
      // to read it as text so we can surface the real reason.
      let reason = err.message;
      if (err.response && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          reason = parsed.error || reason;
        } catch { /* keep err.message */ }
      } else if (err.response?.data?.error) {
        reason = err.response.data.error;
      }
      setError(reason);
      toast.error('Word preview failed', reason);
    } finally {
      setDownloadingDocx(false);
    }
  }

  function handleDocxFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setEditedDocx(null);
      return;
    }
    // Loose extension check — multer enforces size + count server-side.
    if (!/\.docx$/i.test(file.name)) {
      toast.error('Wrong file type', 'Please upload a .docx file');
      e.target.value = '';
      return;
    }
    setEditedDocx(file);
  }

  // Recipients block — preview shows what'll actually go out
  const recipients = preview
    ? (testMode ? preview.recipients.test : preview.recipients.live)
    : null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !busy) onClose();
    }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Bulloch Configuration Request">
        <header className="modal-head">
          <h3>Review &amp; Send · Bulloch Configuration Request</h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        <div className="modal-body">
          {loading && <p className="modal-sub">Loading recipients…</p>}
          {previewError && <p className="error">{previewError}</p>}

          {preview && (
            <>
              <p className="modal-sub">
                Generates the Bulloch Back Office Request from the customer's data,
                converts to PDF, and emails it to the
                {' '}<strong>{preview.cc_group}</strong> CC group from
                {' '}<code>{TEST_DESTINATION}</code>.
              </p>

              <div className="chain-inherit" style={{ marginTop: 0 }}>
                <div className="chain-inherit-head">
                  <span className="chain-inherit-title">
                    Customer: <strong>{preview.customer.company || `#${preview.customer.id}`}</strong>
                  </span>
                  <span className="chain-inherit-hint">
                    Site {preview.customer.site_number || '—'}
                    {preview.customer.fuel_brand && <> · Fuel: {preview.customer.fuel_brand}</>}
                    {preview.customer.pos_type && <> · POS: {preview.customer.pos_type}</>}
                  </span>
                </div>
              </div>

              <label className="form-field form-field-wide" style={{ marginTop: 'var(--space-4)' }}>
                <span>Request Type</span>
                <select value={requestType} onChange={(e) => setRequestType(e.target.value)} disabled={busy || !!sent}>
                  {(preview.request_types || [
                    { value: 'new_client', label: 'New Client Setup' },
                    { value: 'verify_flags', label: 'Verify XML Flags' },
                    { value: 'enable_flags', label: 'Turn On XML Flags (login works, no files)' },
                    { value: 'credentials_update', label: 'Credentials Update' }
                  ]).map((rt) => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                </select>
              </label>

              <h4 className="preview-heading" style={{ marginTop: 'var(--space-4)' }}>
                Review &amp; Send
              </h4>
              <dl className="quote-success-meta">
                <div>
                  <dt>Subject</dt>
                  <dd>
                    {(preview.subjects_by_type?.[requestType]
                      ? preview.subjects_by_type[requestType][testMode ? 'test' : 'live']
                      : (testMode ? preview.subjects?.test : preview.subjects?.live))}
                  </dd>
                </div>
                <div>
                  <dt>PDF Filename</dt>
                  <dd className="cell-mono">{preview.filename}</dd>
                </div>
                <div>
                  <dt>Archive to</dt>
                  <dd className="cell-mono">{preview.archive_path}</dd>
                </div>
                <div>
                  <dt>To</dt>
                  <dd className="cell-mono">{recipients?.to || <span className="cell-muted">—</span>}</dd>
                </div>
                <div>
                  <dt>CC ({recipients?.cc.length || 0})</dt>
                  <dd>
                    {recipients?.cc.length === 0 ? (
                      <span className="cell-muted">—</span>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
                        {recipients.cc.map((email) => (
                          <li key={email} className="cell-mono">{email}</li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>CC Group</dt>
                  <dd className="cell-mono">{preview.cc_group}</dd>
                </div>
              </dl>

              <div className="docx-preview-panel" style={{
                marginTop: 'var(--space-4)',
                padding: 'var(--space-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-sunken)'
              }}>
                <h4 className="preview-heading" style={{ margin: 0, marginBottom: 'var(--space-2)' }}>
                  Word Doc Preview <span className="cell-muted" style={{ fontWeight: 'normal' }}>(optional)</span>
                </h4>
                <p className="modal-sub" style={{ marginTop: 0, marginBottom: 'var(--space-2)' }}>
                  Download the merged Word doc to review. If you need to tweak the wording, edit the file in Word, then upload the edited version below — Send will use your edited doc instead of re-merging from the template.
                </p>
                <div className="form-actions" style={{ marginTop: 0, marginBottom: 'var(--space-2)' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handlePreviewDocx}
                    disabled={downloadingDocx || busy || !!sent}
                  >
                    {downloadingDocx ? 'Preparing…' : 'Preview Word Doc'}
                  </button>
                </div>
                <label className="cell-muted" style={{ display: 'block', fontSize: 'var(--text-small)' }}>
                  Upload edited .docx (optional)
                </label>
                <input
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleDocxFileChange}
                  disabled={busy || !!sent}
                  style={{ marginTop: 'var(--space-1)' }}
                />
                {editedDocx && (
                  <p className="cell-mono" style={{ marginTop: 'var(--space-2)', marginBottom: 0 }}>
                    Will send: <strong>{editedDocx.name}</strong>{' '}
                    <button
                      type="button"
                      onClick={() => setEditedDocx(null)}
                      disabled={busy}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-accent)',
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline'
                      }}
                    >clear</button>
                  </p>
                )}
              </div>

              <div className={`test-mode-panel test-mode-${testMode ? 'on' : 'off'}`}>
                <label className="test-mode-toggle">
                  <input
                    type="checkbox"
                    checked={testMode}
                    onChange={(e) => setTestMode(e.target.checked)}
                    disabled={busy || !!sent}
                  />
                  <span className="test-mode-label">Test Mode</span>
                </label>
                {testMode ? (
                  <p className="test-mode-status">
                    Request will be redirected to <strong>{TEST_DESTINATION}</strong>.
                    No note logged on the customer record.
                  </p>
                ) : (
                  <p className="test-mode-status">
                    <strong>LIVE</strong> — request will be emailed to the
                    {' '}<strong>{preview.cc_group}</strong> group and a note
                    will be logged on the customer record.
                  </p>
                )}
              </div>

              {sent ? (
                // Inline success state — replaces the action buttons and
                // stays visible until the user closes the dialog.
                <div className="send-success-state">
                  <div className="send-success-head">
                    <span className="send-success-icon" aria-hidden="true">✓</span>
                    <span className="send-success-title">Sent successfully</span>
                    {sent.test_mode && <span className="send-success-tag">Test mode</span>}
                  </div>
                  <div className="send-success-row">
                    <span className="send-success-label">Sent to</span>
                    <ul className="send-success-recipients">
                      <li className="cell-mono">{sent.to}</li>
                      {(sent.cc || []).map((email) => (
                        <li key={email} className="cell-mono">{email}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="send-success-row">
                    <span className="send-success-label">Sent at</span>
                    <span className="cell-mono">{new Date(sent.sent_at).toLocaleString()}</span>
                  </div>
                  {pdfFile && (
                    <div className="send-success-row">
                      <span className="send-success-label">PDF</span>
                      <span className="bulloch-pdf-save">
                        <span className="cell-mono">{pdfFile.name}</span>
                        {savedAs
                          ? <span className="bulloch-pdf-saved">✓ Saved</span>
                          : <span className="calls-dim">Save a copy to J:\Bulloch Username</span>}
                        <button type="button" className={savedAs ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'} onClick={() => savePdf(pdfFile)}>
                          {savedAs ? 'Save again…' : 'Save PDF…'}
                        </button>
                      </span>
                    </div>
                  )}
                  <div className="form-actions">
                    <button type="button" className="btn-primary" onClick={onClose}>Close</button>
                  </div>
                </div>
              ) : (
                <>
                  {error && (
                    // Red error state — shown above the buttons, which
                    // stay active so the user can fix the issue and retry.
                    <div className="send-error-state">
                      <div className="send-error-head">
                        <span className="send-error-icon" aria-hidden="true">✕</span>
                        <span className="send-error-title">Send failed</span>
                        {errorCode && <span className="send-error-tag">{errorCode}</span>}
                      </div>
                      <p className="send-error-reason">{error}</p>
                    </div>
                  )}
                  <p className="modal-sub" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
                    {error
                      ? <>Fix the issue above and click <strong>{testMode ? 'Confirm Send (Test)' : 'Confirm Send (LIVE)'}</strong> to try again.</>
                      : testMode
                        ? <>The request will be redirected to <strong>david@xsitegroup.ca</strong> only. Click <strong>Confirm Send</strong> to proceed.</>
                        : <>The request will be emailed live to the recipients above. Click <strong>Confirm Send</strong> to proceed.</>}
                  </p>
                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                      Cancel
                    </button>
                    <button type="button" className="btn-primary" onClick={handleSend} disabled={busy}>
                      {busy
                        ? 'Sending…'
                        : editedDocx
                          ? (testMode ? 'Send Edited Doc (Test)' : 'Send Edited Doc (LIVE)')
                          : testMode
                            ? 'Confirm Send (Test)'
                            : 'Confirm Send (LIVE)'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
